// Alta, baja y modificación del torneo, más las acciones del propio jugador sobre su inscripción.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  Tournament,
  TournamentAction,
  TournamentPlayer,
  UserWithToken,
  WithData,
} from '../api.types';
import { USER_BASIC_FIELDS } from '../fragments';

export const createTournament = async (
  user: Nullable<UserWithToken>,
  input: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation ($input: CreateTournamentInput!) {
        data: createTournament(input: $input) {
          id
        }
      }
    `,
    { input }
  );

export const updateTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  input: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<Tournament>(
    gql`
      mutation ($id: Int!, $input: UpdateTournamentInput!) {
        data: updateTournament(id: $id, input: $input) {
          id
        }
      }
    `,
    { id: tournamentId, input }
  );

export const deleteTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  action: TournamentAction
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation ($id: Int!, $action: TournamentAction!) {
        data: deleteTournament(id: $id, action: $action) {
          id
        }
      }
    `,
    { id: tournamentId, action }
  );

export interface HardDeleteResult {
  success: boolean;
  deletedCount: number;
  errors: string[];
}

export const hardDeleteTournaments = async (
  user: Nullable<UserWithToken>,
  ids: number[]
) =>
  getGraphQLQueryClient(user).request<WithData<HardDeleteResult>>(
    gql`
      mutation ($ids: [Int!]!) {
        data: hardDeleteTournaments(ids: $ids) {
          success
          deletedCount
          errors
        }
      }
    `,
    { ids }
  );

export const resetTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation ($tournamentId: Int!) {
        data: resetTournament(tournamentId: $tournamentId) {
          id
          status
          currentRound
        }
      }
    `,
    { tournamentId }
  );

export const rewindRound = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation ($tournamentId: Int!) {
        data: rewindRound(tournamentId: $tournamentId) {
          id
          status
          currentRound
        }
      }
    `,
    { tournamentId }
  );

/**
 * Self-enroll the current user to a tournament
 * Only works if tournament has acceptsOnlineRegistration = true
 */
export const selfEnrollToTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation ($tournamentId: Int!) {
        data: selfEnrollToTournament(tournamentId: $tournamentId) {
          id
          status
          user {
            ...UserBasicFields
          }
        }
      }
    `,
    { tournamentId }
  );

/**
 * Self-unenroll the current user from a tournament
 * Only works if tournament has not started yet
 * Requires a reason for unenrollment
 */
export const selfUnenrollFromTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  reason: string
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation ($tournamentId: Int!, $reason: String!) {
        data: selfUnenrollFromTournament(
          tournamentId: $tournamentId
          reason: $reason
        )
      }
    `,
    { tournamentId, reason }
  );

/**
 * Self-drop from a started tournament
 * If the player has an unreported match, it's marked as a loss
 */
export const selfDropFromTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      mutation ($tournamentId: Int!) {
        data: selfDropFromTournament(tournamentId: $tournamentId) {
          id
          status
          dropRound
        }
      }
    `,
    { tournamentId }
  );

/**
 * Self-update deck for a tournament
 * Allows enrolled players to set their own deck
 * Deck is private until tournament is CLOSED
 */
export const selfUpdateTournamentDeck = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  deckName: string,
  metaDeckId?: number | null
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation ($tournamentId: Int!, $deckName: String!, $metaDeckId: Int) {
        data: selfUpdateTournamentDeck(
          tournamentId: $tournamentId
          deckName: $deckName
          metaDeckId: $metaDeckId
        ) {
          id
          deckName
          metaDeckId
          user {
            ...UserBasicFields
          }
        }
      }
    `,
    { tournamentId, deckName, metaDeckId }
  );
